export const MANAGER_FEEDBACK_TRAITS = [
  'event_promotion',
  'proposal_generation',
  'listening',
  'empathy_care',
  'goal_clarity',
  'next_connection',
  'context_continuity',
  'risk_detection'
] as const

export type ManagerFeedbackTraitKey = (typeof MANAGER_FEEDBACK_TRAITS)[number]

export type FeedbackDataCoverage = 'empty' | 'thin' | 'useful' | 'rich'
export type ManagerFeedbackSource = 'call' | 'line' | 'overall'

export interface ManagerFeedbackTodo {
  task?: string | null
  assignee?: string | null
  completed?: boolean
  due_date?: string | null
  confidence?: number | null
  evidence?: string | null
}

export interface ManagerFeedbackCall {
  id: string
  liver_id: string
  called_at: string
  duration_seconds: number | null
  status: string
  todos: ManagerFeedbackTodo[] | null
  next_follow_date: string | null
  summary?: string | null
  transcript?: string | null
  error_message?: string | null
  updated_at?: string | null
}

export interface ManagerFeedbackLineImport {
  id: string
  liver_id: string
  file_name: string
  message_count: number
  active_days: number
  date_start: string | null
  date_end: string | null
  primary_manager_name: string | null
  analysis?: unknown
  handoff_note: string | null
  created_at: string
  updated_at: string
}

export interface ManagerFeedbackTraitDefinition {
  label: string
  description: string
}

export interface ManagerFeedbackTraitScore extends ManagerFeedbackTraitDefinition {
  key: ManagerFeedbackTraitKey
  score: number
  confidence: number
  evidence: string[]
}

export interface ManagerFeedbackProfile {
  source: ManagerFeedbackSource
  totalCalls: number
  completedCalls: number
  analyzedCalls: number
  evidenceCount: number
  dataCoverage: FeedbackDataCoverage
  overallScore: number
  traits: ManagerFeedbackTraitScore[]
  traitByKey: Record<ManagerFeedbackTraitKey, ManagerFeedbackTraitScore>
  primaryStrengths: ManagerFeedbackTraitScore[]
  improvementAreas: ManagerFeedbackTraitScore[]
}

export interface ManagerFeedbackSourceSummary {
  source: ManagerFeedbackSource
  label: string
  score: number
  analyzedCount: number
  evidenceCount: number
  dataCoverage: FeedbackDataCoverage
  topTraitLabel: string | null
}

export interface ManagerFeedbackOverview {
  callProfile: ManagerFeedbackProfile
  lineProfile: ManagerFeedbackProfile
  overallProfile: ManagerFeedbackProfile
  sourceSummaries: ManagerFeedbackSourceSummary[]
}

type TraitAccumulator = {
  points: number
  evidence: string[]
}

type KeywordRule = {
  trait: ManagerFeedbackTraitKey
  pattern: RegExp
  points: number
}

type LineFeedbackAnalysis = {
  primaryManager?: {
    name?: string | null
    messageCount?: number | null
    activeDays?: number | null
    callStarts?: number | null
    responseWithin24hRate?: number | null
    styleTags?: string[] | null
  } | null
  managerCandidates?: Array<{
    name?: string | null
    messageCount?: number | null
    styleTags?: string[] | null
  } | null> | null
  topics?: Array<{ topic?: string | null; count?: number | null } | null> | null
  risks?: Array<{ category?: string | null; severity?: string | null; summary?: string | null } | null> | null
  handoffSignals?: unknown[] | null
}

export const MANAGER_FEEDBACK_TRAIT_LABELS: Record<ManagerFeedbackTraitKey, ManagerFeedbackTraitDefinition> = {
  event_promotion: {
    label: 'イベント推進',
    description: 'イベント、順位、告知、応援導線を具体化できているか。'
  },
  proposal_generation: {
    label: '提案生成',
    description: '配信企画や改善策を相手に合わせて提案できているか。'
  },
  listening: {
    label: '傾聴・確認',
    description: '相手の話を拾い、確認、言い換え、深掘りができているか。'
  },
  empathy_care: {
    label: '心理的ケア',
    description: '不安、疲労、葛藤への配慮を示せているか。'
  },
  goal_clarity: {
    label: '目標明確化',
    description: '目標、期限、優先順位、成功条件を明確にできているか。'
  },
  next_connection: {
    label: '次回接点',
    description: '次回フォロー、宿題、連絡タイミングを残せているか。'
  },
  context_continuity: {
    label: '文脈継続',
    description: '前回の文脈や継続課題を踏まえて支援できているか。'
  },
  risk_detection: {
    label: 'リスク検知',
    description: '休止、離脱、炎上、体調、金銭面などの兆候を拾えているか。'
  }
}

const KEYWORD_RULES: KeywordRule[] = [
  { trait: 'event_promotion', pattern: /イベント|順位|ランキング|ランク|告知|応援|導線/, points: 26 },
  { trait: 'proposal_generation', pattern: /提案|企画|改善|試し|試す|やってみ|ネタ|作戦/, points: 32 },
  { trait: 'listening', pattern: /確認|相談|教えて|聞かせ|どう|どんな|何|[？?]/, points: 20 },
  { trait: 'empathy_care', pattern: /不安|体調|疲れ|無理|大丈夫|気持ち|メンタル|安心/, points: 55 },
  { trait: 'goal_clarity', pattern: /目標|期限|優先|成功|何位|いつまで|次まで|数字/, points: 28 },
  { trait: 'next_connection', pattern: /次回|フォロー|宿題|連絡|確認します|確認する|約束/, points: 24 },
  { trait: 'context_continuity', pattern: /前回|続き|前に|以前|この前|前述|継続/, points: 28 },
  { trait: 'risk_detection', pattern: /休止|離脱|炎上|BAN|規約|金銭|換金|体調|不安|トラブル/, points: 18 }
]

export function buildManagerFeedbackProfile(calls: ManagerFeedbackCall[], now: Date): ManagerFeedbackProfile {
  void now
  const completedCalls = calls.filter((call) => call.status === 'complete').length
  const analyzableCalls = calls.filter(hasAnalyzableText)
  const accumulators = emptyAccumulators()
  const callCountByLiver = new Map<string, number>()
  let textChars = 0

  for (const call of calls) {
    callCountByLiver.set(call.liver_id, (callCountByLiver.get(call.liver_id) ?? 0) + 1)
    const text = callText(call)
    textChars += text.length
    if (text.length > 0) {
      for (const rule of KEYWORD_RULES) {
        if (rule.pattern.test(text)) {
          addSignal(accumulators, rule.trait, rule.points, evidenceFromText(text, rule.pattern))
        }
      }
    }

    if (call.next_follow_date) {
      addSignal(accumulators, 'next_connection', 28, `次回接点: ${call.next_follow_date}`)
    }

    for (const todo of call.todos ?? []) {
      const task = todo.task?.trim()
      if (!task) continue
      if (todo.due_date) {
        addSignal(accumulators, 'goal_clarity', 18, `期限付きTODO: ${task}`)
        addSignal(accumulators, 'next_connection', 18, `次回につながるTODO: ${task}`)
      }
      if (todo.confidence != null && todo.confidence >= 0.75) {
        addSignal(accumulators, 'goal_clarity', 10, `高信頼TODO: ${task}`)
      }
    }
  }

  for (const [liverId, count] of callCountByLiver) {
    if (count >= 2) {
      addSignal(accumulators, 'context_continuity', 22, `同一ライバーへの複数回接点: ${liverId}`)
    }
  }

  const coverage = dataCoverage(calls.length, analyzableCalls.length, textChars)
  const coverageMultiplier = coverageConfidenceMultiplier(coverage)
  const traitByKey = Object.fromEntries(
    MANAGER_FEEDBACK_TRAITS.map((key) => {
      const accumulator = accumulators[key]
      const definition = MANAGER_FEEDBACK_TRAIT_LABELS[key]
      const score = clamp(Math.round(accumulator.points), 0, 100)
      const confidence = score === 0 ? 0 : clamp(round2((0.32 + accumulator.evidence.length * 0.17) * coverageMultiplier), 0, 1)
      const trait: ManagerFeedbackTraitScore = {
        key,
        ...definition,
        score,
        confidence,
        evidence: accumulator.evidence.slice(0, 4)
      }
      return [key, trait]
    })
  ) as Record<ManagerFeedbackTraitKey, ManagerFeedbackTraitScore>

  const traits = MANAGER_FEEDBACK_TRAITS.map((key) => traitByKey[key]).sort(compareTraitDesc)
  const scoredTraits = traits.filter((trait) => trait.score > 0)
  const evidenceCount = MANAGER_FEEDBACK_TRAITS.reduce((sum, key) => sum + accumulators[key].evidence.length, 0)
  const overallSource = scoredTraits.length > 0 ? scoredTraits.slice(0, 5) : traits

  return {
    source: 'call',
    totalCalls: calls.length,
    completedCalls,
    analyzedCalls: analyzableCalls.length,
    evidenceCount,
    dataCoverage: coverage,
    overallScore:
      overallSource.length > 0 ? Math.round(overallSource.reduce((sum, trait) => sum + trait.score, 0) / overallSource.length) : 0,
    traits,
    traitByKey,
    primaryStrengths: scoredTraits.slice(0, 3),
    improvementAreas: [...traits].sort(compareTraitAsc).slice(0, 3)
  }
}

export function buildManagerFeedbackOverview(
  calls: ManagerFeedbackCall[],
  lineImports: ManagerFeedbackLineImport[],
  now: Date
): ManagerFeedbackOverview {
  const callProfile = buildManagerFeedbackProfile(calls, now)
  const lineProfile = buildManagerLineFeedbackProfile(lineImports, now)
  const overallProfile = combineFeedbackProfiles([callProfile, lineProfile])

  return {
    callProfile,
    lineProfile,
    overallProfile,
    sourceSummaries: [overallProfile, callProfile, lineProfile].map(sourceSummary)
  }
}

export function buildManagerLineFeedbackProfile(
  lineImports: ManagerFeedbackLineImport[],
  now: Date
): ManagerFeedbackProfile {
  void now
  const accumulators = emptyAccumulators()
  let totalMessages = 0
  let activeDays = 0
  let analyzableImports = 0

  for (const lineImport of lineImports) {
    totalMessages += safeNumber(lineImport.message_count)
    activeDays += safeNumber(lineImport.active_days)
    const analysis = readLineAnalysis(lineImport.analysis)
    const hasAnalysis = hasAnalyzableLineImport(lineImport, analysis)
    if (hasAnalysis) analyzableImports += 1

    if (lineImport.primary_manager_name) {
      addSignal(accumulators, 'context_continuity', 14, `メイン担当候補: ${lineImport.primary_manager_name}`)
    }
    if (lineImport.message_count >= 50 || lineImport.active_days >= 5) {
      addSignal(
        accumulators,
        'context_continuity',
        18,
        `LINE継続: ${lineImport.message_count.toLocaleString()}件 / ${lineImport.active_days}日`
      )
    }
    if (lineImport.active_days >= 7) {
      addSignal(accumulators, 'next_connection', 12, `LINE接点日数: ${lineImport.active_days}日`)
    }
    if (lineImport.handoff_note) {
      addSignal(accumulators, 'context_continuity', 18, lineImport.handoff_note)
    }

    applyLineManagerSignals(accumulators, analysis.primaryManager)
    for (const topic of analysis.topics ?? []) {
      applyLineTopicSignal(accumulators, topic?.topic, topic?.count)
    }
    for (const risk of analysis.risks ?? []) {
      const severity = risk?.severity === 'high' ? 42 : risk?.severity === 'medium' ? 28 : 18
      addSignal(accumulators, 'risk_detection', severity, risk?.summary ?? `LINEリスク: ${risk?.category ?? '要確認'}`)
    }
    if ((analysis.handoffSignals ?? []).length > 0) {
      addSignal(accumulators, 'context_continuity', 24, `LINE引き継ぎシグナル: ${analysis.handoffSignals?.length}件`)
      addSignal(accumulators, 'risk_detection', 14, `担当変更の可能性: ${analysis.handoffSignals?.length}件`)
    }
  }

  const coverage = lineDataCoverage(lineImports.length, analyzableImports, totalMessages, activeDays)
  return buildProfileFromAccumulators({
    source: 'line',
    totalCalls: lineImports.length,
    completedCalls: lineImports.length,
    analyzedCalls: analyzableImports,
    evidenceCount: MANAGER_FEEDBACK_TRAITS.reduce((sum, key) => sum + accumulators[key].evidence.length, 0),
    dataCoverage: coverage,
    accumulators
  })
}

function combineFeedbackProfiles(profiles: ManagerFeedbackProfile[]): ManagerFeedbackProfile {
  const accumulators = emptyAccumulators()
  const sourcePrefixes: Record<ManagerFeedbackSource, string> = {
    overall: '総合',
    call: '電話',
    line: 'LINE'
  }

  for (const profile of profiles) {
    for (const trait of profile.traits) {
      if (trait.score <= 0) continue
      addSignal(accumulators, trait.key, weightedTraitPoints(trait, profile.source), `${sourcePrefixes[profile.source]}: ${trait.label} ${trait.score}`)
      for (const evidence of trait.evidence.slice(0, 2)) {
        addSignal(accumulators, trait.key, 0, `${sourcePrefixes[profile.source]}: ${evidence}`)
      }
    }
  }

  return buildProfileFromAccumulators({
    source: 'overall',
    totalCalls: profiles.reduce((sum, profile) => sum + profile.totalCalls, 0),
    completedCalls: profiles.reduce((sum, profile) => sum + profile.completedCalls, 0),
    analyzedCalls: profiles.reduce((sum, profile) => sum + profile.analyzedCalls, 0),
    evidenceCount: profiles.reduce((sum, profile) => sum + profile.evidenceCount, 0),
    dataCoverage: combinedCoverage(profiles.map((profile) => profile.dataCoverage)),
    accumulators
  })
}

function buildProfileFromAccumulators({
  source,
  totalCalls,
  completedCalls,
  analyzedCalls,
  evidenceCount,
  dataCoverage,
  accumulators
}: {
  source: ManagerFeedbackSource
  totalCalls: number
  completedCalls: number
  analyzedCalls: number
  evidenceCount: number
  dataCoverage: FeedbackDataCoverage
  accumulators: Record<ManagerFeedbackTraitKey, TraitAccumulator>
}): ManagerFeedbackProfile {
  const coverageMultiplier = coverageConfidenceMultiplier(dataCoverage)
  const traitByKey = Object.fromEntries(
    MANAGER_FEEDBACK_TRAITS.map((key) => {
      const accumulator = accumulators[key]
      const definition = MANAGER_FEEDBACK_TRAIT_LABELS[key]
      const score = clamp(Math.round(accumulator.points), 0, 100)
      const confidence = score === 0 ? 0 : clamp(round2((0.32 + accumulator.evidence.length * 0.17) * coverageMultiplier), 0, 1)
      return [
        key,
        {
          key,
          ...definition,
          score,
          confidence,
          evidence: accumulator.evidence.slice(0, 4)
        }
      ]
    })
  ) as Record<ManagerFeedbackTraitKey, ManagerFeedbackTraitScore>
  const traits = MANAGER_FEEDBACK_TRAITS.map((key) => traitByKey[key]).sort(compareTraitDesc)
  const scoredTraits = traits.filter((trait) => trait.score > 0)
  const overallSource = scoredTraits.length > 0 ? scoredTraits.slice(0, 5) : traits

  return {
    source,
    totalCalls,
    completedCalls,
    analyzedCalls,
    evidenceCount,
    dataCoverage,
    overallScore:
      overallSource.length > 0 ? Math.round(overallSource.reduce((sum, trait) => sum + trait.score, 0) / overallSource.length) : 0,
    traits,
    traitByKey,
    primaryStrengths: scoredTraits.slice(0, 3),
    improvementAreas: [...traits].sort(compareTraitAsc).slice(0, 3)
  }
}

function sourceSummary(profile: ManagerFeedbackProfile): ManagerFeedbackSourceSummary {
  return {
    source: profile.source,
    label: sourceLabel(profile.source),
    score: profile.overallScore,
    analyzedCount: profile.analyzedCalls,
    evidenceCount: profile.evidenceCount,
    dataCoverage: profile.dataCoverage,
    topTraitLabel: profile.primaryStrengths[0]?.label ?? null
  }
}

function sourceLabel(source: ManagerFeedbackSource): string {
  switch (source) {
    case 'overall':
      return '総合'
    case 'call':
      return '電話'
    case 'line':
      return 'LINE'
  }
}

function readLineAnalysis(value: unknown): LineFeedbackAnalysis {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const src = value as LineFeedbackAnalysis
  return {
    primaryManager: src.primaryManager && typeof src.primaryManager === 'object' ? src.primaryManager : null,
    managerCandidates: Array.isArray(src.managerCandidates) ? src.managerCandidates : [],
    topics: Array.isArray(src.topics) ? src.topics : [],
    risks: Array.isArray(src.risks) ? src.risks : [],
    handoffSignals: Array.isArray(src.handoffSignals) ? src.handoffSignals : []
  }
}

function hasAnalyzableLineImport(lineImport: ManagerFeedbackLineImport, analysis: LineFeedbackAnalysis): boolean {
  return Boolean(
    lineImport.message_count > 0 ||
      lineImport.primary_manager_name ||
      lineImport.handoff_note ||
      analysis.primaryManager ||
      (analysis.topics?.length ?? 0) > 0 ||
      (analysis.risks?.length ?? 0) > 0 ||
      (analysis.handoffSignals?.length ?? 0) > 0
  )
}

function applyLineManagerSignals(
  accumulators: Record<ManagerFeedbackTraitKey, TraitAccumulator>,
  primaryManager: LineFeedbackAnalysis['primaryManager']
): void {
  if (!primaryManager) return
  const name = primaryManager.name?.trim() || 'メイン担当候補'
  if (safeNumber(primaryManager.messageCount) > 0) {
    addSignal(accumulators, 'context_continuity', 14, `${name}: LINE発言 ${primaryManager.messageCount}件`)
  }
  if (safeNumber(primaryManager.activeDays) >= 7) {
    addSignal(accumulators, 'next_connection', 16, `${name}: 継続接点 ${primaryManager.activeDays}日`)
  }
  if (safeNumber(primaryManager.callStarts) > 0) {
    addSignal(accumulators, 'next_connection', 10, `${name}: LINE通話開始 ${primaryManager.callStarts}回`)
  }
  if (primaryManager.responseWithin24hRate != null && primaryManager.responseWithin24hRate >= 0.8) {
    addSignal(accumulators, 'next_connection', 16, `${name}: 24時間以内返信率 ${Math.round(primaryManager.responseWithin24hRate * 100)}%`)
  }

  for (const tag of primaryManager.styleTags ?? []) {
    applyLineStyleTagSignal(accumulators, tag, name)
  }
}

function applyLineStyleTagSignal(
  accumulators: Record<ManagerFeedbackTraitKey, TraitAccumulator>,
  tag: string,
  managerName: string
): void {
  if (tag === '即レス型') {
    addSignal(accumulators, 'next_connection', 28, `${managerName}: 即レス型`)
    addSignal(accumulators, 'listening', 16, `${managerName}: 即レス型`)
  } else if (tag === '電話説明型') {
    addSignal(accumulators, 'listening', 12, `${managerName}: 電話説明型`)
    addSignal(accumulators, 'next_connection', 10, `${managerName}: 電話説明型`)
  } else if (tag === '資料共有型') {
    addSignal(accumulators, 'proposal_generation', 22, `${managerName}: 資料共有型`)
  } else if (tag === '肯定フィードバック型') {
    addSignal(accumulators, 'empathy_care', 34, `${managerName}: 肯定フィードバック型`)
  } else if (tag === '事務対応型') {
    addSignal(accumulators, 'risk_detection', 18, `${managerName}: 事務対応型`)
    addSignal(accumulators, 'context_continuity', 12, `${managerName}: 事務対応型`)
  }
}

function applyLineTopicSignal(
  accumulators: Record<ManagerFeedbackTraitKey, TraitAccumulator>,
  topic: string | null | undefined,
  count: number | null | undefined
): void {
  const suffix = count ? ` ${count}件` : ''
  switch (topic) {
    case 'event':
      addSignal(accumulators, 'event_promotion', 30, `LINE話題: イベント${suffix}`)
      break
    case 'schedule_call':
      addSignal(accumulators, 'next_connection', 24, `LINE話題: 日程・通話${suffix}`)
      addSignal(accumulators, 'listening', 12, `LINE話題: 日程・通話${suffix}`)
      break
    case 'payment_tax':
      addSignal(accumulators, 'risk_detection', 20, `LINE話題: 支払・税務${suffix}`)
      addSignal(accumulators, 'context_continuity', 14, `LINE話題: 支払・税務${suffix}`)
      break
    case 'rights_rules':
      addSignal(accumulators, 'risk_detection', 28, `LINE話題: 権利・ルール${suffix}`)
      break
    case 'motivation':
      addSignal(accumulators, 'empathy_care', 28, `LINE話題: モチベーション${suffix}`)
      break
    case 'onboarding':
      addSignal(accumulators, 'proposal_generation', 20, `LINE話題: オンボーディング${suffix}`)
      addSignal(accumulators, 'context_continuity', 12, `LINE話題: オンボーディング${suffix}`)
      break
  }
}

function weightedTraitPoints(trait: ManagerFeedbackTraitScore, source: ManagerFeedbackSource): number {
  const sourceWeight = source === 'call' ? 0.58 : source === 'line' ? 0.52 : 1
  const confidenceWeight = trait.confidence > 0 ? 0.72 + trait.confidence * 0.28 : 0.62
  return Math.round(trait.score * sourceWeight * confidenceWeight)
}

function combinedCoverage(coverages: FeedbackDataCoverage[]): FeedbackDataCoverage {
  const ranks: Record<FeedbackDataCoverage, number> = { empty: 0, thin: 1, useful: 2, rich: 3 }
  const score = coverages.reduce((sum, coverage) => sum + ranks[coverage], 0)
  if (score >= 5) return 'rich'
  if (score >= 3) return 'useful'
  if (score >= 1) return 'thin'
  return 'empty'
}

function lineDataCoverage(
  totalImports: number,
  analyzableImports: number,
  totalMessages: number,
  activeDays: number
): FeedbackDataCoverage {
  if (totalImports === 0 || analyzableImports === 0 || totalMessages === 0) return 'empty'
  if (analyzableImports < 2 || totalMessages < 120 || activeDays < 7) return 'thin'
  if (analyzableImports < 5 || totalMessages < 800 || activeDays < 30) return 'useful'
  return 'rich'
}

function emptyAccumulators(): Record<ManagerFeedbackTraitKey, TraitAccumulator> {
  return Object.fromEntries(
    MANAGER_FEEDBACK_TRAITS.map((trait) => [trait, { points: 0, evidence: [] }])
  ) as unknown as Record<ManagerFeedbackTraitKey, TraitAccumulator>
}

function addSignal(
  accumulators: Record<ManagerFeedbackTraitKey, TraitAccumulator>,
  trait: ManagerFeedbackTraitKey,
  points: number,
  evidence: string
): void {
  const accumulator = accumulators[trait]
  accumulator.points = clamp(accumulator.points + points, 0, 100)
  const normalized = evidence.trim()
  if (normalized && !accumulator.evidence.includes(normalized)) {
    accumulator.evidence.push(normalized)
  }
}

function hasAnalyzableText(call: ManagerFeedbackCall): boolean {
  return callText(call).length >= 20
}

function callText(call: ManagerFeedbackCall): string {
  return [call.summary, call.transcript]
    .map((value) => value?.trim() ?? '')
    .filter(Boolean)
    .join('\n')
}

function evidenceFromText(text: string, pattern: RegExp): string {
  const match = pattern.exec(text)
  if (!match?.[0]) return truncate(text)
  const index = Math.max(0, match.index - 24)
  return truncate(text.slice(index, match.index + match[0].length + 42))
}

function dataCoverage(totalCalls: number, analyzableCalls: number, textChars: number): FeedbackDataCoverage {
  if (totalCalls === 0 || analyzableCalls === 0 || textChars === 0) return 'empty'
  if (analyzableCalls < 3 || textChars < 1200) return 'thin'
  if (analyzableCalls < 8 || textChars < 8000) return 'useful'
  return 'rich'
}

function coverageConfidenceMultiplier(coverage: FeedbackDataCoverage): number {
  switch (coverage) {
    case 'rich':
      return 1
    case 'useful':
      return 0.82
    case 'thin':
      return 0.58
    case 'empty':
      return 0
  }
}

function compareTraitDesc(a: ManagerFeedbackTraitScore, b: ManagerFeedbackTraitScore): number {
  return b.score - a.score || b.confidence - a.confidence || a.label.localeCompare(b.label, 'ja')
}

function compareTraitAsc(a: ManagerFeedbackTraitScore, b: ManagerFeedbackTraitScore): number {
  return a.score - b.score || a.confidence - b.confidence || a.label.localeCompare(b.label, 'ja')
}

function truncate(value: string, max = 84): string {
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1)}…`
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function round2(value: number): number {
  return Math.round(value * 100) / 100
}

function safeNumber(value: number | null | undefined): number {
  return Number.isFinite(value) ? Number(value) : 0
}
